import { CartAddEvent, ThemeEvents } from '@theme/events';

/**
 * ============================================================================
 *  PASHION STUDIO — Logique de conversion du panier (drawer)
 *  Grille responsive sans scroll. Protégé du morphing via
 *  data-skip-subtree-update (respecté par assets/morph.js).
 * ============================================================================
 */

const money = (cents, withSymbol = true) => {
  const value = (Number(cents) / 100).toLocaleString('fr-FR', {
    minimumFractionDigits: 2,
    maximumFractionDigits: 2,
  });
  return withSymbol ? `€${value}` : `${value} €`;
};

const escapeHtml = (str) => {
  const div = document.createElement('div');
  div.textContent = str == null ? '' : String(str);
  return div.innerHTML;
};

const routeRoot = () =>
  (window.Shopify && window.Shopify.routes && window.Shopify.routes.root) || '/';

async function fetchCart() {
  try {
    const res = await fetch(`${routeRoot()}cart.js`, {
      headers: { Accept: 'application/json' },
      cache: 'no-store',
    });
    if (!res.ok) return null;
    return await res.json();
  } catch (e) {
    return null;
  }
}

/* ===========================================================================
 * 1. BARRE LIVRAISON OFFERTE
 * ======================================================================== */
class FreeShippingBar extends HTMLElement {
  connectedCallback() {
    this.threshold = parseInt(this.dataset.threshold || '0', 10);
    this.unlockedText = this.dataset.unlockedText || 'Livraison offerte débloquée';
    this.prefixText = this.dataset.prefixText || 'Plus que';
    this.suffixText = this.dataset.suffixText || 'pour la livraison offerte';

    this._onUpdate = this._onUpdate.bind(this);
    document.addEventListener(ThemeEvents.cartUpdate, this._onUpdate);
  }

  disconnectedCallback() {
    document.removeEventListener(ThemeEvents.cartUpdate, this._onUpdate);
  }

  async _onUpdate() {
    const cart = await fetchCart();
    if (cart) this.render(cart.total_price);
  }

  render(total) {
    if (!this.threshold) return;

    const pct = Math.min(100, Math.max(0, (total / this.threshold) * 100));
    const remaining = Math.max(0, this.threshold - total);
    const nowUnlocked = total >= this.threshold;
    const wasUnlocked = this.classList.contains('fsb--unlocked');

    const fill = this.querySelector('[data-fsb-fill]');
    if (fill) fill.style.setProperty('--fsb-pct', `${pct}%`);

    const label = this.querySelector('[data-fsb-label]');

    if (nowUnlocked) {
      if (!wasUnlocked) {
        this.classList.add('fsb--unlocked');
        if (label) {
          label.innerHTML =
            `<span class="fsb__spark" aria-hidden="true">✦</span> ${escapeHtml(this.unlockedText)}`;
        }
        this._celebrate();
      }
    } else {
      if (wasUnlocked) {
        this.classList.remove('fsb--unlocked');
      }
      if (label) {
        label.innerHTML =
          `<span class="fsb__prefix">${escapeHtml(this.prefixText)}</span> ` +
          `<span class="fsb__amount" data-fsb-amount>${money(remaining, false)}</span> ` +
          `<span class="fsb__suffix">${escapeHtml(this.suffixText)}</span>`;
        const amount = label.querySelector('[data-fsb-amount]');
        if (amount) {
          void amount.offsetWidth;
          amount.classList.add('fsb__amount--flash');
        }
      }
    }
  }

  _celebrate() {
    const track = this.querySelector('.fsb__track');
    if (!track) return;
    track.classList.remove('fsb__track--pop');
    void track.offsetWidth;
    track.classList.add('fsb__track--pop');
  }
}

/* ===========================================================================
 * 2. RECOMMANDATIONS ("Vous aimerez peut-être aussi")
 * ======================================================================== */
class CartRecommendations extends HTMLElement {
  connectedCallback() {
    this.limit = parseInt(this.dataset.limit || '4', 10);
    this.recRoute = this.dataset.recommendationsUrl || `${routeRoot()}recommendations/products`;
    this.sectionId = this.dataset.sectionId || null;
    this.scroller = this.querySelector('[data-reco-scroller]');
    this.titleEl = this.querySelector('[data-reco-title]');

    this._anchorId = this.dataset.anchorProductId || null;
    this._loadedAnchor = null;
    this._inflight = null;
    this._cartProductIds = new Set();

    this._onUpdate = this._onUpdate.bind(this);
    this._onClick = this._onClick.bind(this);
    this.addEventListener('click', this._onClick);
    document.addEventListener(ThemeEvents.cartUpdate, this._onUpdate);

    if (this._anchorId) {
      this._syncCartIds().then(() => this._load(this._anchorId));
    } else {
      this._onUpdate();
    }
  }

  disconnectedCallback() {
    this.removeEventListener('click', this._onClick);
    document.removeEventListener(ThemeEvents.cartUpdate, this._onUpdate);
  }

  async _syncCartIds() {
    const cart = await fetchCart();
    this._cartProductIds = new Set(
      (cart?.items || []).map((i) => String(i.product_id))
    );
    return cart;
  }

  async _onUpdate() {
    const cart = await this._syncCartIds();
    if (!cart) return;

    if (!cart.items || cart.items.length === 0) {
      this._teardown();
      return;
    }

    const anchor = String(cart.items[0].product_id);

    if (anchor !== this._loadedAnchor) {
      await this._load(anchor);
    } else {
      this._filterAlreadyInCart();
    }
  }

  async _load(anchorId) {
    if (!anchorId || !this.scroller) return;
    this._loadedAnchor = anchorId;

    if (this._inflight) this._inflight.abort();
    const controller = new AbortController();
    this._inflight = controller;

    this._renderSkeletons();

    try {
      const url =
        `${this.recRoute}.json?product_id=${encodeURIComponent(anchorId)}` +
        `&limit=${this.limit}&intent=related`;
      const res = await fetch(url, {
        headers: { Accept: 'application/json' },
        signal: controller.signal,
      });
      if (!res.ok) throw new Error(`reco ${res.status}`);
      const data = await res.json();

      let products = (data.products || []).filter((p) => {
        if (!p || p.available === false) return false;
        if (this._cartProductIds.has(String(p.id))) return false;
        return true;
      });

      if (products.length === 0) {
        this._teardown();
        return;
      }

      this._show();
      this.scroller.innerHTML = products
        .map((p, i) => this._cardHTML(p, i))
        .join('');
    } catch (err) {
      if (err.name === 'AbortError') return;
      this._teardown();
    } finally {
      if (this._inflight === controller) this._inflight = null;
    }
  }

  _cardHTML(product, index) {
    const variant =
      (product.variants || []).find((v) => v.available) ||
      (product.variants || [])[0];
    if (!variant) return '';

    const price = variant.price;
    const compare = variant.compare_at_price;
    const onSale = compare && compare > price;

    const img =
      product.featured_image ||
      (product.images && product.images[0]) ||
      '';

    const imgTag = img
      ? `<img class="reco__img" src="${img}" alt="${escapeHtml(product.title)}" loading="lazy" width="400" height="400">`
      : `<span class="reco__img reco__img--empty" aria-hidden="true"></span>`;

    const priceBlock = onSale
      ? `<span class="reco__price reco__price--sale">${money(price)}</span>` +
        `<span class="reco__compare">${money(compare)}</span>`
      : `<span class="reco__price">${money(price)}</span>`;

    return `
      <article class="reco__card" style="--i:${index};" data-product-id="${product.id}">
        <a class="reco__media" href="${product.url}">
          ${imgTag}
        </a>
        <button
          type="button"
          class="reco__add"
          data-variant-id="${variant.id}"
          aria-label="Ajouter ${escapeHtml(product.title)} au panier"
        >
          <span class="reco__add-icon" aria-hidden="true">
            <svg viewBox="0 0 24 24" width="20" height="20" fill="none" stroke="currentColor" stroke-width="2" stroke-linecap="round" stroke-linejoin="round"><path d="M12 5v14M5 12h14"/></svg>
          </span>
          <span class="reco__add-spinner" aria-hidden="true"></span>
        </button>
        <div class="reco__info">
          <a class="reco__name cart-primary-typography" href="${product.url}">${escapeHtml(product.title)}</a>
          <div class="reco__prices cart-secondary-typography">${priceBlock}</div>
        </div>
      </article>`;
  }

  _filterAlreadyInCart() {
    if (!this.scroller) return;
    const cards = this.scroller.querySelectorAll('.reco__card');
    let visible = 0;
    cards.forEach((card) => {
      const pid = String(card.dataset.productId || '');
      if (this._cartProductIds.has(pid)) {
        card.classList.add('reco__card--hidden');
      } else {
        card.classList.remove('reco__card--hidden');
        visible += 1;
      }
    });
    if (visible === 0) this._teardown();
    else this._show();
  }

  _renderSkeletons() {
    if (!this.scroller) return;
    this._show();
    this.scroller.innerHTML = Array.from({ length: 4 })
      .map(() => `<div class="reco__skeleton" aria-hidden="true"></div>`)
      .join('');
  }

  _show() {
    this.hidden = false;
    this.classList.remove('cart-reco--empty');
  }

  _teardown() {
    this._loadedAnchor = null;
    if (this.scroller) this.scroller.innerHTML = '';
    this.hidden = true;
    this.classList.add('cart-reco--empty');
  }

  async _onClick(event) {
    const button = event.target.closest('.reco__add');
    if (!button) return;

    event.preventDefault();
    event.stopPropagation();

    if (button.getAttribute('aria-busy') === 'true') return;
    const variantId = button.dataset.variantId;
    if (!variantId) return;

    button.setAttribute('aria-busy', 'true');

    try {
      const sections = this.sectionId ? [this.sectionId] : [];
      const res = await fetch(`${routeRoot()}cart/add.js`, {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
          Accept: 'application/json',
        },
        body: JSON.stringify({
          items: [{ id: variantId, quantity: 1 }],
          sections,
          sections_url: window.location.pathname,
        }),
      });

      if (!res.ok) {
        let msg = 'Ajout impossible';
        try {
          const errData = await res.json();
          msg = errData.description || errData.message || msg;
        } catch (_) {}
        throw new Error(msg);
      }

      const data = await res.json();

      const iconWrap = button.querySelector('.reco__add-icon');
      if (iconWrap) {
        iconWrap.innerHTML =
          '<svg viewBox="0 0 24 24" width="20" height="20" fill="none" stroke="currentColor" stroke-width="2.5" stroke-linecap="round" stroke-linejoin="round"><path d="M20 6 9 17l-5-5"/></svg>';
      }
      button.setAttribute('aria-busy', 'false');

      const card = button.closest('.reco__card');
      if (card) {
        card.classList.add('reco__card--added');
        window.setTimeout(() => {
          card.classList.add('reco__card--hidden');
        }, 500);
      }

      document.dispatchEvent(
        new CartAddEvent({}, this.sectionId || 'cart-recommendations', {
          source: 'cart-recommendations',
          itemCount: data && data.item_count,
          sections: data && data.sections,
        })
      );
    } catch (error) {
      button.setAttribute('aria-busy', 'false');
      const iconWrap = button.querySelector('.reco__add-icon');
      if (iconWrap) {
        iconWrap.innerHTML =
          '<svg viewBox="0 0 24 24" width="20" height="20" fill="none" stroke="currentColor" stroke-width="2" stroke-linecap="round" stroke-linejoin="round"><path d="M12 5v14M5 12h14"/></svg>';
      }
      console.error('[cart-recommendations]', error);
    }
  }
}

if (!customElements.get('free-shipping-bar')) {
  customElements.define('free-shipping-bar', FreeShippingBar);
}
if (!customElements.get('cart-recommendations')) {
  customElements.define('cart-recommendations', CartRecommendations);
}